import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import React from "react";
import { styled } from "@mui/material/styles";

const Root = styled("div")({
  display: "flex",
  flexShrink: 0,
  position: "relative",
});
const Content = styled("div")({
  height: "100%",
  overflow: "hidden",
  width: "100%",
});
/**
 * The seam's rule, at rest and while the handle holds the pointer or focus.
 *
 * Named because the grip is positioned off them. `divider` is translucent — `rgba(0, 0, 0, 0.12)` —
 * so whatever sits under the rule shows through it, and the line reads darker there than along the
 * rest of its length. First that was the whole pill, and once its background was out of the way it
 * was the pill's top and bottom borders, as two darker ticks. Nothing of the grip goes under the
 * rule, in either state, which is the only arrangement a translucent line stays even through.
 */
const RULE_WIDTH = 2;
const RULE_HOVER_WIDTH = 3;

/**
 * The drag target, inside the panel rather than straddling its edge.
 *
 * It used to sit at `right: -8` — half its 16px over the panel, half over whatever the panel was
 * beside. The outer half is an absolutely positioned box at `z-index: 9`, so on a proposal tab it
 * both drew its grip over the fee table's first column and swallowed every click that landed in the
 * table's leftmost 8px. Held inside the panel, the whole target belongs to the panel it resizes and
 * nothing beside it loses a strip.
 *
 * The seam is where the divider goes, so the rule is pinned to this element's right edge and the
 * grip is pulled back to sit just inside it.
 */
const Handle = styled("div")(({ theme }) => ({
  bottom: 0,
  cursor: "ew-resize",
  outline: 0,
  position: "absolute",
  right: 0,
  top: 0,
  touchAction: "none",
  width: 16,
  zIndex: 9,
  "&::after": {
    backgroundColor: theme.palette.divider,
    bottom: 0,
    content: '""',
    position: "absolute",
    right: 0,
    top: 0,
    transition: "background-color 0.15s ease, width 0.15s ease",
    width: RULE_WIDTH,
    // Only earns its keep mid-transition, while the grip is still stepping aside.
    zIndex: 2,
  },
  "&:hover::after, &:focus-visible::after": {
    backgroundColor: theme.palette.primary.main,
    width: RULE_HOVER_WIDTH,
  },
  "&:hover .ResizablePanel-grip, &:focus-visible .ResizablePanel-grip": {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    right: RULE_HOVER_WIDTH,
  },
}));
const Grip = styled("span")(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  // Square against the seam and rounded away from it, with no border at all on the seam side: the
  // rule is that edge, and a border there would only be a second line beside the first.
  borderBottomLeftRadius: theme.shape.borderRadius,
  borderBottomRightRadius: 0,
  borderRightWidth: 0,
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: 0,
  color: theme.palette.text.secondary,
  display: "flex",
  height: 32,
  justifyContent: "center",
  pointerEvents: "none",
  position: "absolute",
  // Against the seam rather than centred on it, so the grip stays inside the panel it belongs to
  // (see `Handle`) — and beside the rule rather than beneath it, so the line keeps one weight. It
  // gives up the pixel the rule gains on hover, over the same 150ms, so the two stay touching.
  right: RULE_WIDTH,
  top: "50%",
  transform: "translate(0, -50%)",
  transition: "border-color 0.15s ease, color 0.15s ease, right 0.15s ease",
  width: 20,
  zIndex: 1,
}));

type ResizablePanelProps = React.PropsWithChildren<{
  className?: string;
  initialWidth?: number;
  minWidth?: number;
  minRemainingWidth?: number;
}>;

export default function ResizablePanel(props: Readonly<ResizablePanelProps>) {
  const { children, className } = props;
  const minWidth = props.minWidth ?? 375;
  // A panel opens at its narrowest. Templates and document selectors are pickers and the tab's own
  // editor is the work, so the room belongs to the editor until a reader drags some of it back —
  // which is one gesture, where recovering the width an over-wide default took was not.
  const initialWidth = props.initialWidth ?? minWidth;
  const minRemainingWidth = props.minRemainingWidth ?? 320;

  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const handleRef = React.useRef<HTMLDivElement | null>(null);
  const pointerIdRef = React.useRef<number | null>(null);
  const startXRef = React.useRef(0);
  const startWidthRef = React.useRef(initialWidth);
  const widthRef = React.useRef(initialWidth);

  const applyWidth = React.useCallback((nextWidth: number) => {
    widthRef.current = nextWidth;
    if (rootRef.current) {
      rootRef.current.style.width = `${nextWidth}px`;
    }
  }, []);

  const getMaxWidth = React.useCallback(() => {
    if (!rootRef.current?.parentElement) return Number.POSITIVE_INFINITY;

    const parentRect = rootRef.current.parentElement.getBoundingClientRect();
    const rootRect = rootRef.current.getBoundingClientRect();
    const siblingsWidth = Array.from(rootRef.current.parentElement.children)
      .filter((element) => element !== rootRef.current)
      .reduce((totalWidth, element) => {
        const styles = globalThis.getComputedStyle(element);
        const minWidthValue = Number.parseFloat(styles.minWidth);
        const flexGrow = Number.parseFloat(styles.flexGrow);
        let reservedWidth = (element).getBoundingClientRect().width;

        if (Number.isFinite(minWidthValue) && minWidthValue > 0) {
          reservedWidth = minWidthValue;
        } else if (Number.isFinite(flexGrow) && flexGrow > 0) {
          reservedWidth = 0;
        }

        const marginLeft = Number.parseFloat(styles.marginLeft);
        const marginRight = Number.parseFloat(styles.marginRight);

        return totalWidth + reservedWidth + marginLeft + marginRight;
      }, 0);

    return Math.max(
      minWidth,
      Math.min(
        parentRect.right - rootRect.left,
        parentRect.width - siblingsWidth - minRemainingWidth,
      ),
    );
  }, [minRemainingWidth, minWidth]);

  const updateHandleRange = React.useCallback(() => {
    if (!handleRef.current) return;

    const maxWidth = getMaxWidth();
    handleRef.current.setAttribute("aria-valuemin", `${minWidth}`);
    handleRef.current.setAttribute("aria-valuenow", `${Math.round(widthRef.current)}`);
    if (Number.isFinite(maxWidth)) {
      handleRef.current.setAttribute("aria-valuemax", `${Math.round(maxWidth)}`);
    } else {
      handleRef.current.removeAttribute("aria-valuemax");
    }
  }, [getMaxWidth, minWidth]);

  React.useLayoutEffect(() => {
    applyWidth(Math.max(initialWidth, minWidth));
    updateHandleRange();
  }, [applyWidth, initialWidth, minWidth, updateHandleRange]);

  const stopDragging = React.useCallback((handleArg?: HTMLDivElement | null) => {
    // Read the fallback in the body rather than as a parameter default: the React Compiler cannot
    // reorder a member expression used as a default value and bails out on the whole component.
    const handle = handleArg === undefined ? handleRef.current : handleArg;
    if (pointerIdRef.current !== null && handle?.hasPointerCapture(pointerIdRef.current)) {
      handle.releasePointerCapture(pointerIdRef.current);
    }
    pointerIdRef.current = null;
    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");
  }, []);

  const handlePointerDown = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!rootRef.current) return;

    pointerIdRef.current = event.pointerId;
    startXRef.current = event.clientX;
    startWidthRef.current = rootRef.current.getBoundingClientRect().width;

    event.currentTarget.setPointerCapture(event.pointerId);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    event.preventDefault();
  }, []);

  const handlePointerMove = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId || !rootRef.current) return;

    const nextWidth = Math.min(
      Math.max(minWidth, startWidthRef.current + (event.clientX - startXRef.current)),
      getMaxWidth(),
    );
    applyWidth(nextWidth);
    updateHandleRange();
  }, [applyWidth, getMaxWidth, minWidth, updateHandleRange]);

  const handleKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 48 : 16;
    let nextWidth: number | undefined;

    if (event.key === "ArrowLeft") nextWidth = widthRef.current - step;
    if (event.key === "ArrowRight") nextWidth = widthRef.current + step;
    if (event.key === "Home") nextWidth = minWidth;
    if (event.key === "End") nextWidth = getMaxWidth();
    if (nextWidth === undefined) return;

    event.preventDefault();
    applyWidth(Math.min(Math.max(minWidth, nextWidth), getMaxWidth()));
    updateHandleRange();
  }, [applyWidth, getMaxWidth, minWidth, updateHandleRange]);

  React.useEffect(() => {
    const clampToAvailableWidth = () => {
      if (!rootRef.current) return;

      const nextWidth = Math.min(widthRef.current, getMaxWidth());
      // Only write when the answer actually moved: the observer below watches the row this panel is
      // in, and a panel that rewrote its width on every notification could keep feeding itself.
      if (nextWidth === widthRef.current) return;
      applyWidth(nextWidth);
      updateHandleRange();
    };

    /**
     * The row narrows without the window changing size — on a proposal page, opening the comments
     * drawer takes 400px of it — so a panel dragged wide beforehand stayed wide and overflowed the
     * row it no longer fits (#4964). Watching the row itself catches that as well as a window resize.
     */
    const row = rootRef.current?.parentElement;
    let rowObserver: ResizeObserver | undefined;
    if (row && typeof ResizeObserver !== "undefined") {
      rowObserver = new ResizeObserver(clampToAvailableWidth);
      rowObserver.observe(row);
    }

    globalThis.addEventListener("resize", clampToAvailableWidth);
    return () => {
      rowObserver?.disconnect();
      globalThis.removeEventListener("resize", clampToAvailableWidth);
    };
  }, [applyWidth, getMaxWidth, updateHandleRange]);

  React.useEffect(() => () => {
    if (pointerIdRef.current !== null) {
      stopDragging();
    }
  }, [stopDragging]);

  const handlePointerUp = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    stopDragging(event.currentTarget);
  }, [stopDragging]);

  const handlePointerCancel = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    stopDragging(event.currentTarget);
  }, [stopDragging]);

  return (
    <Root className={className} ref={rootRef}>
      <Content>{children}</Content>
      <Handle
        aria-label="Resize panel"
        aria-orientation="vertical"
        onLostPointerCapture={(event) => {
          if (pointerIdRef.current === event.pointerId) {
            stopDragging(event.currentTarget);
          }
        }}
        onFocus={updateHandleRange}
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        onPointerCancel={handlePointerCancel}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        ref={handleRef}
        role="separator"
        tabIndex={0}
        title="Drag or use the arrow keys to resize"
      >
        <Grip className="ResizablePanel-grip">
          <DragIndicatorIcon aria-hidden fontSize="small" />
        </Grip>
      </Handle>
    </Root>
  );
}
